import React, { useState, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useContent } from '../hooks/useContent';
import defaultContent from '../data/defaultContent.json';
import RichText from './RichText';
import { trackContactClick } from '../utils/analytics';
import './navbar.css';

// Touch browsers commonly fire a synthetic mouseenter right before click (for
// :hover compatibility), so wiring both hover-to-open and click-to-toggle on
// the same element made a single tap open then immediately re-close itself —
// the dropdown only ever seemed to respond on the second tap. Only wire up
// the hover handlers on devices that actually have a real pointer to hover
// with; touch-only devices get pure click/tap toggling with no interference.
const supportsHover = typeof window !== 'undefined' && window.matchMedia
  ? window.matchMedia('(hover: hover) and (pointer: fine)').matches
  : true;

// Helper to convert any phone number into an international WhatsApp link (e.g. https://wa.me/12482751077)
const getPhoneWhatsAppLink = (phone, fallbackLink) => {
  if (!phone) return fallbackLink || 'https://wa.me/16477616277';
  const digits = String(phone).replace(/[^0-9]/g, '');
  if (!digits) return fallbackLink || 'https://wa.me/16477616277';
  const fullNumber = digits.length === 10 ? `1${digits}` : digits;
  return `https://wa.me/${fullNumber}`;
};

const Navbar = () => {
  const { content } = useContent('navbar');
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const closeTimeoutRef = useRef(null);

  const handleLinkClick = () => {
    setIsMenuOpen(false);
  };

  // Closing on a short delay (instead of the instant mouseleave) makes the
  // dropdown forgiving of the brief moment the cursor crosses the gap
  // between the "Services" link and the menu below it while moving toward it.
  const openServicesMenu = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setIsServicesOpen(true);
  };

  const closeServicesMenu = () => {
    closeTimeoutRef.current = setTimeout(() => setIsServicesOpen(false), 250);
  };

  const toggleServicesMenu = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setIsServicesOpen((prev) => !prev);
  };

  const toggleMobileMenu = () => {
    if (!isMenuOpen) {
      setIsServicesOpen(true);
    }
    setIsMenuOpen((prev) => !prev);
  };

  const defaultNav = defaultContent.navbar || {};
  const logo = content?.logo || defaultNav.logo || "/images/02.png";
  const logoAlt = content?.logoAlt || defaultNav.logoAlt || "Venus Global Technology Logo";
  const rawMenuItems = (content?.menuItems && content.menuItems.length > 0) ? content.menuItems : (defaultNav.menuItems || []);
  
  // Guarantee that Careers is always present in top navigation
  let ensuredItems = [...rawMenuItems];
  if (!ensuredItems.some((item) => item.path === '/careers' || item.label?.toLowerCase() === 'careers')) {
    const blogsIndex = ensuredItems.findIndex((item) => item.path === '/blogs' || item.label?.toLowerCase() === 'blogs');
    const contactIndex = ensuredItems.findIndex((item) => item.path === '/contact' || item.label?.toLowerCase() === 'contact us');
    const insertIndex = blogsIndex >= 0 ? blogsIndex : (contactIndex >= 0 ? contactIndex : ensuredItems.length);
    ensuredItems.splice(insertIndex, 0, {
      label: 'Careers',
      path: '/careers'
    });
  }

  // Guarantee that required services (/software-data-ai and /staff-augmentation) are always present
  const menuItems = ensuredItems.map((item) => {
    if (item.submenu && item.submenu.length > 0) {
      const existingPaths = new Set(item.submenu.map((s) => s.path));
      const updatedSubmenu = [...item.submenu];

      const requiredServices = [
        { label: 'Software & Data AI', path: '/software-data-ai' },
        { label: 'Staff Augmentation', path: '/staff-augmentation' }
      ];

      requiredServices.forEach((req) => {
        if (!existingPaths.has(req.path)) {
          const erpIndex = updatedSubmenu.findIndex((s) => s.path === '/erp-ai');
          if (erpIndex !== -1) {
            updatedSubmenu.splice(erpIndex + 1, 0, req);
          } else {
            updatedSubmenu.push(req);
          }
          existingPaths.add(req.path);
        }
      });

      return { ...item, submenu: updatedSubmenu };
    }
    return item;
  });

  const callText = content?.callText || defaultNav.callText || "Call Any Time";
  const phoneNumber = content?.phoneNumber || defaultNav.phoneNumber || "248-275-1077";
  const phoneWhatsAppUrl = getPhoneWhatsAppLink(phoneNumber, content?.whatsappLink || defaultNav.whatsappLink);
  const whatsappLink = content?.whatsappLink || defaultNav.whatsappLink || phoneWhatsAppUrl;

  // Build flattened menu items array for mobile view (omitting parent "Services" item header)
  const mobileMenuItems = [];
  menuItems.forEach((item) => {
    if (item.submenu && item.submenu.length > 0) {
      item.submenu.forEach((sub) => {
        mobileMenuItems.push({
          label: sub.label,
          path: sub.path,
          isExternal: sub.isExternal
        });
      });
    } else {
      mobileMenuItems.push(item);
    }
  });

  return (
    <nav className={`navbar ${isMenuOpen ? 'menu-open' : ''}`}>
      <div className={`nav-container ${isMenuOpen ? 'menu-open' : ''}`}>
        
        {/* Left Side Logo */}
        <div className="nav-logo">
          <Link to="/" className="logo-link" onClick={handleLinkClick}>
            <img
              src={logo}
              alt={logoAlt || "Venus Global Tech"}
              className="logo-img"
              width="168"
              height="38"
              fetchpriority="high"
            />
          </Link>
        </div>

        {/* Center Navigation Menu */}
        <div className={`nav-menu ${isMenuOpen ? 'open' : ''}`}>
          
          {/* Desktop Navigation List */}
          <ul className="nav-list desktop-nav-list">
            {menuItems.map((item) => (
              <li
                key={item.label}
                className={`nav-item ${item.submenu ? 'dropdown' : ''} ${location.pathname === item.path ? 'active' : ''}`}
                {...(item.submenu && supportsHover
                  ? { onMouseEnter: openServicesMenu, onMouseLeave: closeServicesMenu }
                  : {})}
              >
                {item.submenu ? (
                  <>
                    <a
                      href="#"
                      className="nav-link"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        toggleServicesMenu();
                      }}
                    >
                      <RichText html={item.label} as="span" />
                      <i className="fas fa-chevron-down nav-chevron"></i>
                    </a>
                    <div className={`dropdown-menu ${isServicesOpen ? 'open' : ''}`}>
                      {item.submenu.map((sub) => {
                        const isErpAi = sub.label?.trim() === 'ERP AI' || sub.path === '/erp-ai' || sub.path === 'https://vgt-erp-ai-2.vercel.app/' || sub.path === 'https://erp.venusglobaltech.com/';
                        const targetPath = isErpAi ? '/erp-ai' : sub.path;
                        const isExternal = !isErpAi && (sub.isExternal || /^https?:\/\//.test(targetPath));

                        if (isExternal) {
                          return (
                            <a
                              key={sub.label}
                              href={targetPath}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="dropdown-item"
                              onClick={(e) => {
                                e.stopPropagation();
                                setIsServicesOpen(false);
                                setIsMenuOpen(false);
                              }}
                            >
                              {sub.label}
                            </a>
                          );
                        }
                        return (
                          <Link
                            key={sub.label}
                            to={targetPath}
                            className="dropdown-item"
                            onClick={(e) => {
                              e.stopPropagation();
                              setIsServicesOpen(false);
                              setIsMenuOpen(false);
                            }}
                          >
                            {sub.label}
                          </Link>
                        );
                      })}
                    </div>
                  </>
                ) : (
                  <Link to={item.path} className="nav-link" onClick={handleLinkClick}>
                    {item.label}
                  </Link>
                )}
              </li>
            ))}
          </ul>

          {/* Mobile Navigation List (Flat top-level panels, Services parent omitted) */}
          <ul className="nav-list mobile-nav-list">
            {mobileMenuItems.map((item) => {
              const isErpAi = item.label?.trim() === 'ERP AI' || item.path === '/erp-ai' || item.path === 'https://vgt-erp-ai-2.vercel.app/' || item.path === 'https://erp.venusglobaltech.com/';
              const targetPath = isErpAi ? '/erp-ai' : item.path;
              const isExternal = !isErpAi && (item.isExternal || targetPath?.startsWith('http'));
              const isActive = location.pathname === targetPath;

              if (isExternal) {
                return (
                  <li key={item.label} className={`nav-item ${isActive ? 'active' : ''}`}>
                    <a
                      href={targetPath}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="nav-link"
                      onClick={(e) => {
                        handleLinkClick();
                        window.open(targetPath, '_blank', 'noopener,noreferrer');
                      }}
                    >
                      {item.label}
                    </a>
                  </li>
                );
              }

              return (
                <li key={item.label} className={`nav-item ${isActive ? 'active' : ''}`}>
                  <Link to={targetPath} className="nav-link" onClick={handleLinkClick}>
                    {item.label}
                  </Link>
                </li>
              );
            })}
          </ul>

          {/* Mobile-only CTA */}
          <div className="mobile-only">
            <div className="nav-mobile-cta">
              <button 
                className="nav-btn-primary full-width" 
                onClick={() => {
                  handleLinkClick();
                  const target = whatsappLink || phoneWhatsAppUrl;
                  trackContactClick({
                    type: 'whatsapp',
                    destination: target,
                    label: 'Navbar Mobile Book a Call'
                  });
                  window.open(target, '_blank');
                }}
              >
                <span>Book a Call</span>
                <i className="fas fa-phone-alt"></i>
              </button>
            </div>
          </div>
        </div>

        {/* Right Side Call Info & Book a Call Button */}
        <div className="nav-right-actions">
          
          {/* Call Any Time Block */}
          <div 
            className="nav-call-block" 
            onClick={() => {
              trackContactClick({
                type: 'whatsapp',
                destination: phoneWhatsAppUrl,
                label: `Navbar Call Any Time (${phoneNumber})`
              });
              window.open(phoneWhatsAppUrl, '_blank');
            }}
            role="button"
            tabIndex={0}
            aria-label={`Chat on WhatsApp with ${phoneNumber}`}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                trackContactClick({
                  type: 'whatsapp',
                  destination: phoneWhatsAppUrl,
                  label: `Navbar Call Any Time (${phoneNumber})`
                });
                window.open(phoneWhatsAppUrl, '_blank');
              }
            }}
          >
            <div className="nav-call-icon-badge">
              <i className="fas fa-phone-alt"></i>
            </div>
            <div className="nav-call-text-wrap">
              <span className="nav-call-label">{callText || 'Call Any Time'}</span>
              <span className="nav-call-number">{phoneNumber}</span>
            </div>
          </div>

          {/* Divider */}
          <div className="nav-action-divider"></div>

          {/* Book a Call CTA Pill Button */}
          <button 
            className="nav-btn-primary"
            onClick={() => {
              const target = whatsappLink || phoneWhatsAppUrl;
              trackContactClick({
                type: 'whatsapp',
                destination: target,
                label: 'Navbar Desktop Book a Call'
              });
              window.open(target, '_blank');
            }}
          >
            Book a Call
          </button>

        </div>

        {/* Mobile Right Controls: Phone Icon Badge + Hamburger Toggle */}
        <div className="nav-mobile-controls">
          <button 
            className="nav-mobile-phone-btn"
            onClick={() => {
              trackContactClick({
                type: 'whatsapp',
                destination: phoneWhatsAppUrl,
                label: 'Navbar Mobile Phone Icon'
              });
              window.open(phoneWhatsAppUrl, '_blank');
            }}
            aria-label="Call Us"
          >
            <i className="fas fa-phone-alt"></i>
          </button>

          <button
            className={`nav-toggle ${isMenuOpen ? 'open' : ''}`}
            onClick={toggleMobileMenu}
            aria-label="Toggle Navigation"
          >
            <span className="nav-toggle-bar"></span>
            <span className="nav-toggle-bar"></span>
            <span className="nav-toggle-bar"></span>
          </button>
        </div>

        {/* Must stay inside .nav-container: that element's backdrop-filter
            creates its own stacking context, which traps .nav-menu's
            z-index inside it. As a sibling of .nav-container instead (both
            direct children of .navbar), this overlay would paint above the
            entire container — menu included — no matter how high the
            menu's z-index is, since that number only competes against other
            elements sharing its own stacking context. */}
        {isMenuOpen && <div className="nav-overlay" onClick={() => setIsMenuOpen(false)}></div>}

      </div>
    </nav>
  );
};

export default Navbar;
